/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useRef, useState }  from 'react';
import {
    object, func, bool, array, string, number,
} from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import {
    Button,
    Dialog, DialogContent, DialogTitle, ExpansionPanel, ExpansionPanelDetails, ExpansionPanelSummary, IconButton, useMediaQuery,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import ArrowForwardIosIcon from '@material-ui/icons/ArrowForwardIosTwoTone';
import ArrowBackIosIcon from '@material-ui/icons/ArrowBackIosTwoTone';
import ReactMarkdown from 'react-markdown';
import ReactHtmlParser from 'react-html-parser';

const styles = (theme) => ({
    modalBox: {
        maxWidth: '950px',
        width: '100%',
        borderRadius: 'inherit',
        '& .MuiDialogTitle-root': {
            padding: '0px 24px',
            boxShadow: '0px 3px 3px rgb(47 47 47 / 15%)',
            '@media only screen and (max-width: 600px) ': {
                padding: '10px',
            },
        },
        '@media only screen and (max-width: 600px) ': {
            margin: '2rem 0.6rem',
            minHeight: 'calc(100% - 30px)',
        },
    },
    dialogTitle: {
        fontSize: '1.25rem',
        lineHeight: '1.75rem',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        fontWeight: 700,
        '@media only screen and (max-width: 1023px) ': {
            fontSize: '1rem',
            lineHeight: '1.25rem',
        },
        '@media only screen and (max-width: 400.5px) ': {
            fontSize: '0.875rem',
            lineHeight: '1rem',
        },

    },
    singleContent: {
        display: 'flex',
        gap: '17px',
        '&::-webkit-scrollbar-thumb': {
            borderRadius: '10px',
            backgroundColor: 'rgba(156, 156, 156, 1)',  // scroll bar color
        },
        '&::-webkit-scrollbar': {  /* Works on scrollbar for Chrome, Safari and Opera */
            height: '2px',
            width: '10px',
            backgroundColor: 'transparent',
        },
        '&::-webkit-scrollbar-track': {
            borderRadius: '10px',
            backgroundColor: 'transparent',
        },
        '@media only screen and (max-width: 600px) ': {
            display: 'block',
        },
    },
    productName: {
        fontSize: '1.5rem',
        lineHeight: '2.5rem',
        fontWeight: 700,
        '@media only screen and (max-width: 1023px) ': {
            fontSize: '1.25rem',
            lineHeight: '1.875rem',
        },
    },
    price: {
        fontSize: '1.25rem',
        lineHeight: '1.75rem',
        fontWeight: 700,
        '@media only screen and (max-width: 1023px) ': {
            fontSize: '1.125rem',
        },
        '@media only screen and (max-width: 600px) ': {
            fontSize: '1rem',
            lineHeight: '1.25rem',
        },
    },

    button: {
        color: theme.palette.cms?.white || 'white',
        backgroundColor: theme.palette.cms?.black || '#000000',
        borderRadius: 'inherit',
        padding: '10px 118px',
        fontSize: '1rem',
        lineHeight: '1.25rem',
        fontWeight: 700,
        textTransform: 'capitalize',
        '@media only screen and (max-width: 700px) ': {
            width: '100%',
            padding: '10px ',
        },
        '&:hover': {
            color: theme.palette.cms?.white || 'white',
            backgroundColor: theme.palette.cms?.black || '#000000',
        },
        '&.Mui-disabled': {
            backgroundColor: theme.palette.cms?.lightGray || '#F2F2F2',
        },
    },
    arrowButton: {
        backgroundColor: theme.palette?.cms?.activeButtonColor || '#E8F1ED',
        borderRadius: '50%',
        width: '28px',
        height: '28px',
        '@media only screen and (max-width: 380.5px) ': {
            width: '20px',
            height: '20px',
        },
        '& svg': {
            width: '0.7em',
            fill: theme.palette.cms?.black || '#2f2f2f',
            '@media only screen and (max-width: 380.5px) ': {
                width: '0.5em',
            },
        },
        '&:hover': {
            backgroundColor: theme.palette?.cms?.activeButtonColor || '#E8F1ED',
        },
    },
    iconContainer: {
        display: 'flex',
        gap: '1.25rem',
        justifyContent: 'center',
        alignItems: 'center',
        '@media only screen and (max-width: 600.5px) ': {
            gap: '0.6rem',
        },
        '& .MuiIconButton-root.Mui-disabled': {
            backgroundColor: '#F2F2F2',
            '& svg': {
                fill: 'rgba(0, 0, 0, 0.26)',
            },
        },
    },
    buttonMainContainer: {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '38px',
        marginTop: '21px',
        '@media only screen and (max-width: 1023.5px) ': {
            gap: '16px',
        },
        '@media only screen and (max-width: 600.5px) ': {
            justifyContent: 'center',
        },
    },
    dialogContent: {
        padding: '30px 20px 29px 32px',
        '@media only screen and (max-width: 600.5px) ': {
            padding: '1.25rem',
        },
    },
    descriptionWrapper: {
        width: '100%',
    },
    imageContainer: {
        width: '70%',
        '@media only screen and (max-width: 767.5px) ': {
            width: '100%',
        },
        '& img': {
            width: '100%',
            aspectRatio: ' 138/151',
        },
    },
    buttonMobileContainer: {
        position: 'sticky',
        bottom: '0px',
        width: 'auto',
        textAlign: 'center',
        margin: '1.25rem',
    },
    closeBtn: {
        '@media only screen and (max-width: 600.5px) ': {
            marginLeft: '10px',
            padding: '0px',
        },
        '& svg': {
            fill: theme.palette.cms?.black || '#2f2f2f',
            '@media only screen and (max-width: 380.5px) ': {
                width: '0.8em',
            },
        },
    },
    panelRoot: {
        boxShadow: 'none',
        backgroundColor: 'inherit',
        borderBottom: `2px solid ${theme.palette.cms?.productDetailsSummary || '#607767'}`,
        '&:before': {
            display: 'none',
        },
        '&.Mui-expanded': {
            margin: '0px',
            paddingBottom: '10px',
            borderBottom: `2px solid ${theme.palette.cms?.tertiary_hover}`,
        },
        '& .Mui-expanded': {
            margin: '0px',
            minHeight: 'auto',
        },
    },
    summaryContent: {
        margin: '0px !important',
        justifyContent: 'space-between',
        alignItems: 'center',
        '& h2': {
            margin: '0px',
            '@media only screen and (max-width: 600px) ': {
                fontSize: '1.125rem',
                lineHeight: '1.675rem',
            },
        },
    },
    contentContainer: {
        overflowY: 'auto',
        maxHeight: '265px',
        '&::-webkit-scrollbar-thumb': {
            borderRadius: '10px',
            backgroundColor: 'rgba(156, 156, 156, 1)',  // scroll bar color
        },
        '&::-webkit-scrollbar': {  /* Works on scrollbar for Chrome, Safari and Opera */
            height: '2px',
            width: '10px',
            backgroundColor: 'transparent',
        },
        '&::-webkit-scrollbar-track': {
            borderRadius: '10px',
            backgroundColor: 'transparent',
        },
        '@media only screen and (max-width: 600px) ': {
            maxHeight: 'none',
        },
    },
    summaryRoot: {
        minHeight: 'auto',
        padding: '2px 0px',

    },
    detailsRoot: {
        display: 'block',

    },
    itemContentStyleMark: {
        '& ul': {
            margin: '0px',
            paddingLeft: '25px',
        },
    },
    monthName: {
        fontSize: '1.5rem',
        lineHeight: '2.5rem',
        fontWeight: 700,
        '@media only screen and (max-width: 600px) ': {
            fontSize: '1.25rem',
            lineHeight: '1.875rem',
        },
    },
    removeIcon: {
        '& path:first-child': {
            fill: theme.palette.headerColor || '#E8F1ED',
            opacity: 1,
        },
    },
    iconImage: {
        display: 'flex',
        width: '26px',
        height: '26px',
    },
});

const GraphqlClubProductDetails = ({
    classes, open, setOpen, data, reciepientProduct, setData, title, indexMatch, monthDetails, onclick, buttonContent,  disabled, skuMonths, selectedSku,
}) => {
    let changeClubMonthLength = 0;
    reciepientProduct.map((matchObject) => {
        const tempSelectedMonth = skuMonths[matchObject?.month];
        const currentClubLength = Number(selectedSku?.clubLength) || 0;
        const nextMonthSameLength = tempSelectedMonth?.findIndex((monthSku) => Number(monthSku?.clubLength) === currentClubLength);
        if (nextMonthSameLength >= 0) {
            changeClubMonthLength = nextMonthSameLength;
        }
    });
    const [contentActive, setContentActive] = useState(true);
    const [descriptionAction, setDescriptionActive] = useState(false);
    const scrollRef = useRef();
    const innerScroll = useRef();
    const isMobile = useMediaQuery('(max-width: 600.5px)');
    const addIcon =  <img src="https://images.contentstack.io/v3/assets/blt89dbf1c763ec00a6/blt137ba229a33bc496/63e9d22b167c064eec4b3f36/add_icon.svg?width=26&height=26&auto=webp`" alt="add_icon"  className={classes.iconImage} />;
    const removeIcon = <img src="https://images.contentstack.io/v3/assets/blt89dbf1c763ec00a6/bltce80760950cd82b9/63e9d22b0185c11a442d5fc4/remove_icon.svg?width=26&height=26&auto=webp`" alt="remove_icon" className={classes.iconImage} />;
    const singleProductDetailTemplate = () => {
        const buildImgPath = (path, name) => `${path + name}x.jpg`;
        const newName = data.name || '';
        const imageURL = buildImgPath(data.image.path, data.image.name) || 'https://images.contentstack.io/v3/assets/blt95e0138311135214/bltffae3c14fd3096e8/62e01cf13bba407090b248ee/Missing_Image.png?auto=webp';
        const productItemContents = data.longDescription || '';
        const productItemDescription = data.shortDescription || '';
        return (
            <>
                <div className={classes.contentWrapper}>
                    <div className={classes.singleContent}>
                        <div className={classes.imageContainer}>
                            <img
                                className={classes.image}
                                src={`${imageURL}?auto=webp`}
                                alt={newName}
                            />
                        </div>
                        <div className={classes.descriptionWrapper}>
                            {monthDetails &&  <div className={classes.monthName}>{ReactHtmlParser(data?.month)}</div>}
                            <div className={classes.productName}>{ReactHtmlParser(newName)}</div>
                            {!monthDetails && <div className={classes.price}>+ ${data?.upSellCalculatedPrice} </div>}
                            <div className={classes.contentContainer}>
                                {productItemContents &&  (
                                    <ExpansionPanel classes={{ root: classes.panelRoot }} square expanded={contentActive}  onClick={() => setContentActive(!contentActive)}>
                                        <ExpansionPanelSummary classes={{ root: classes.summaryRoot, content: classes.summaryContent }}>
                                            <h2>Contents</h2>
                                            <span className={classes.icons} role="presentation">{(contentActive) ? removeIcon : addIcon}</span>

                                        </ExpansionPanelSummary>
                                        <ExpansionPanelDetails className={classes.detailsRoot} data-testId="content">
                                            <ReactMarkdown source={productItemContents} escapeHtml={false} className={classes.itemContentStyleMark}  />
                                        </ExpansionPanelDetails>
                                    </ExpansionPanel>
                                )}
                                {productItemContents &&  (
                                    <ExpansionPanel classes={{ root: classes.panelRoot }} square  expanded={descriptionAction} onClick={() => setDescriptionActive(!descriptionAction)}>
                                        <ExpansionPanelSummary classes={{ root: classes.summaryRoot, content: classes.summaryContent }}>
                                            <h2>Description</h2>
                                            <span className={classes.icons} role="presentation">{(descriptionAction) ? removeIcon : addIcon}</span>

                                        </ExpansionPanelSummary>
                                        <ExpansionPanelDetails className={classes.detailsRoot} data-testId="description">
                                            <ReactMarkdown source={productItemDescription} escapeHtml={false} className={classes.itemContentStyleMark} />
                                        </ExpansionPanelDetails>
                                    </ExpansionPanel>
                                )}
                            </div>
                            {!isMobile && (
                                <div className={classes.buttonMainContainer}>
                                    <Button
                                        className={classes.button}
                                        disabled={disabled}
                                        onClick={() => {
                                            if (monthDetails) {
                                                onclick(skuMonths[data?.month]?.[changeClubMonthLength]);
                                                setOpen(false);
                                            } else {
                                                onclick();
                                            }
                                        }}
                                    >
                                        {buttonContent}
                                    </Button>
                                </div>
                            )}
                        </div>

                    </div>
                </div>
            </>
        );
    };
    return (
        <Dialog
            classes={{
                paper: classes.modalBox,
            }}
            open={open}
            onClose={() => setOpen(false)}
        >
            <DialogTitle>
                <div className={classes.dialogTitle}>
                    {title}
                    <div className={classes.iconContainer}>
                        <IconButton
                            className={classes.arrowButton}
                            aria-label="previous"
                            data-testId="previouse-icon"
                            disabled={indexMatch <= 0}
                            onClick={() => {
                                setData(reciepientProduct[indexMatch - 1]);
                                if (scrollRef.current) {
                                    scrollRef.current.scrollTop = 0;
                                }
                                if (innerScroll.current) {   // for desktop view inner content scroll
                                    innerScroll.current.scrollTop = 0;
                                }
                            }}
                        >
                            <ArrowBackIosIcon />
                        </IconButton>
                        <IconButton
                            className={classes.arrowButton}
                            aria-label="next"
                            disabled={indexMatch === reciepientProduct?.length - 1}
                            data-testId="forward-icon"
                            onClick={() => {
                                setData(reciepientProduct[indexMatch + 1]);
                                if (scrollRef.current) {
                                    scrollRef.current.scrollTop = 0;
                                }
                                if (innerScroll.current) { // for desktop view inner content scroll
                                    innerScroll.current.scrollTop = 0;
                                }
                            }}
                        >
                            <ArrowForwardIosIcon />
                        </IconButton>
                        <IconButton
                            className={classes.closeBtn}
                            aria-label="Close"
                            onClick={() => {
                                setOpen(false);
                            }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </div>
                </div>
            </DialogTitle>
            <DialogContent className={classes.dialogContent}>
                {singleProductDetailTemplate()}
            </DialogContent>
            {isMobile &&  (
                <div className={classes.buttonMobileContainer}>
                    <Button
                        className={classes.button}
                        disabled={disabled}
                        onClick={() => {
                            if (monthDetails) {
                                onclick(skuMonths[data?.month]?.[changeClubMonthLength]);
                                setOpen(false);
                            } else {
                                onclick();
                            }
                        }}
                    >
                        {buttonContent}
                    </Button>
                </div>
            )}
        </Dialog>

    );
};

GraphqlClubProductDetails.defaultProps = {
    monthDetails: false,
    onclick: () => {},
    disabled: false,
    buttonContent: 'Select',
    skuMonths: [],
    selectedSku: '',
};

GraphqlClubProductDetails.propTypes = {
    classes: object.isRequired,
    data: object.isRequired,
    disabled: bool,
    open: bool.isRequired,
    setOpen: func.isRequired,
    reciepientProduct: array.isRequired,
    setData: func.isRequired,
    title: string.isRequired,
    indexMatch: number.isRequired,
    monthDetails: bool,
    onclick: func,
    buttonContent: string,
    skuMonths: array,
    selectedSku: string,

};

export default withStyles(styles)(GraphqlClubProductDetails);
